import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Select } from '@thoughtspot/radiant-react/widgets/select';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import React from 'react';
import { VariableType } from "../../../../services/generated/graphql-types";
import { PRIMARY_ORG_ID, TranslationsKeys, } from '../../variable-interface';
import styles from '../create-edit-modal.module.scss';
export const AssignValuesStep = ({ orgMappings, orgOptionsGroups, variableType, rowErrors, isOrgsLoading, sensitive, createdOrgId = '0', onAddRow, onRemoveRow, onMappingNameChange, onValueChange, getFilteredOrgOptions, }) => {
    const isReadOnly = !!(createdOrgId && createdOrgId !== PRIMARY_ORG_ID);
    const { t } = useTranslation();
    const orgMappingConfig = {
        label: t(TranslationsKeys.ORG_MAPPING_LABEL),
        headerLabel: t(TranslationsKeys.ORG_MAPPING_HEADER_LABEL),
        placeholder: t(TranslationsKeys.ORG_MAPPING_PLACEHOLDER),
        optionsGroups: orgOptionsGroups,
        tooltip: t(TranslationsKeys.TOOLTIP_ORG_MAPPING),
    };
    const getInputPlaceholder = (mapping) => {
        if (sensitive && mapping.isExisting && !mapping.value) {
            return '********';
        }
        return t(TranslationsKeys.ORG_MAPPING_VALUE_PLACEHOLDER);
    };
    const getErrorMessage = () => {
        const hasOrgErrors = Object.values(rowErrors).some(err => err.orgId);
        const hasValueErrors = Object.values(rowErrors).some(err => err.value);
        const hasTimezoneFormatErrors = Object.values(rowErrors).some(err => err.timezoneFormat);
        if (hasTimezoneFormatErrors) {
            return t(TranslationsKeys.ORG_MAPPING_VALIDATION_ERROR_INVALID_TIMEZONE);
        }
        if (hasOrgErrors && hasValueErrors) {
            return t(TranslationsKeys.ORG_MAPPING_VALIDATION_ERROR_MISSING_BOTH);
        }
        if (hasOrgErrors) {
            return t(TranslationsKeys.ORG_MAPPING_VALIDATION_ERROR_MISSING_ORGS);
        }
        if (hasValueErrors) {
            return t(TranslationsKeys.ORG_MAPPING_VALIDATION_ERROR_MISSING_VALUES);
        }
        return null;
    };
    const errorMessage = getErrorMessage();
    return (React.createElement(React.Fragment, null, !isOrgsLoading && (React.createElement(Vertical, { className: styles.step2Container },
        errorMessage && (React.createElement(Alert.Banner, { message: errorMessage, isVisible: true, type: BannerAlertType.ERROR, className: styles.errorBanner })),
        React.createElement(Vertical, { spacing: "d", className: styles.orgMappingSection },
            React.createElement(Horizontal, { spacing: "a", className: styles.labelWithIcon },
                React.createElement(Typography, { variant: "content-label-subhead", className: styles.sectionLabel }, orgMappingConfig.label),
                React.createElement(Icon, { className: styles.infoIcon, id: IconID.INFORMATION_CIRCLE, size: IconSize.XSMALL, color: IconColor.GRAY, "data-tooltip-content": t(TranslationsKeys.TOOLTIP_ORG_MAPPING) })),
            React.createElement(Vertical, { spacing: "c", className: styles.tableAndButtonWrapper },
                React.createElement(Vertical, { className: styles.mappingTable },
                    React.createElement("div", { className: styles.tableHeader },
                        React.createElement("div", { className: styles.tableHeaderCell }, orgMappingConfig.headerLabel),
                        React.createElement("div", { className: styles.tableHeaderCell }, variableType ===
                            VariableType.FormulaVariable ? (React.createElement(Horizontal, { spacing: "a", className: styles.headerWithIcon },
                            React.createElement("span", null, t(TranslationsKeys.FORMULA_HINT)),
                            React.createElement(Icon, { className: styles.headerInfoIcon, id: IconID.INFORMATION, size: IconSize.XSMALL }))) : (t(TranslationsKeys.VALUE_LABEL))),
                        React.createElement("div", { className: styles.tableHeaderCellAction }, "\u00A0")),
                    orgMappings.map(mapping => {
                        const hasOrgError = rowErrors[mapping.id]?.orgId || false;
                        const hasValueError = rowErrors[mapping.id]?.value ||
                            rowErrors[mapping.id]
                                ?.timezoneFormat ||
                            false;
                        return (React.createElement("div", { key: mapping.id, className: styles.tableRow },
                            React.createElement("div", { className: styles.tableCell },
                                React.createElement(Select.Dropdown, { selectedOptions: mapping.orgId
                                        ? [mapping.orgId]
                                        : [], onChange: (selection) => onMappingNameChange(mapping.id, selection?.id ?? ''), optionsGroups: getFilteredOrgOptions(mapping.id, mapping.orgId), placeholder: orgMappingConfig.placeholder, hasError: hasOrgError, "data-testid": `org-select-${mapping.id}` })),
                            React.createElement("div", { className: styles.tableCell },
                                React.createElement(Input.Text, { value: mapping.value, onChange: e => onValueChange(mapping.id, e.target.value), placeholder: getInputPlaceholder(mapping), hasError: hasValueError, "data-testid": `value-input-${mapping.id}` })),
                            React.createElement("div", { className: styles.tableCellAction },
                                React.createElement(Button, { type: "tertiary", icon: IconID.CROSS, onClick: () => onRemoveRow(mapping.id), isDisabled: orgMappings.length === 1, ariaLabel: "Remove row", "data-testid": `remove-row-${mapping.id}` }))));
                    })),
                React.createElement(Button, { type: "tertiary", icon: IconID.PLUS, iconColor: IconColor.BLUE, onClick: onAddRow, isDisabled: isReadOnly, className: styles.addRowButton, "data-testid": "add-row-button", text: t(TranslationsKeys.ADD_ROW_BUTTON) })))))));
};
//# sourceMappingURL=assign-values-step.component.js.map